/* eslint-disable @typescript-eslint/no-explicit-any */

import { useQuery } from '@tanstack/react-query'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselApi
} from '~/components/UI/shadcn/carousel'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle
} from '~/components/UI/shadcn/dialog'
import Separator from '~/components/UI/General/Separator/Separator'
import Tag from '~/components/UI/General/Tag/Tag'
import { numberWithCommas } from '~/utils/misc'
import { getEntryById } from '../../data'
import Image from '~/components/Helpers/Image'
import { useEffect, useState } from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import CarouselControls from '~/components/UI/General/CarouselControls/CarouselControls'

const AccommodationCard = ({ item, index }: any) => {
  const [api, setApi] = useState<CarouselApi>()

  // const { status, data, error } = useQuery({
  const { data } = useQuery({
    queryKey: ['gallery', item.gallery.sys.id],
    queryFn: async () => getEntryById(item.gallery.sys.id)
  })

  useEffect(() => {
    // If no api, return
    if (!api) return
  }, [api])

  return (
    <div
      key={item.sys.id + '-' + index}
      className='gap-s sm:gap-l py-m border-b-blue-d flex flex-col border-b first:pt-0 sm:flex-row sm:items-center'
    >
      <div className='bg-grey-l relative aspect-square w-full max-w-[310px] shrink-0'>
        <Carousel
          setApi={setApi}
          className=''
          opts={{
            align: 'start',
            loop: true
          }}
        >
          <CarouselContent className={'aspect-square'}>
            {data &&
              data?.image.map((item: any, index: Index) => (
                <CarouselItem
                  key={`${item.asset.sys.id}-${index}`}
                  className='relative'
                >
                  <Image
                    image={item}
                    lazyLoad={index === 0 ? false : true}
                    priority={index === 0 ? 'high' : 'low'}
                  />
                </CarouselItem>
              ))}
          </CarouselContent>

          <div className='p-xs absolute right-0 bottom-0 z-10 flex w-fit'>
            <ClientOnly>
              {() => (
                <CarouselControls
                  current={0}
                  count={data?.image.length}
                  colour={'white'}
                  setCurrent={(index: number) => {
                    if (api) {
                      api.scrollTo(index) // Scroll to the selected slide
                    }
                  }}
                  hidePager={true}
                />
              )}
            </ClientOnly>
          </div>
        </Carousel>

        {data?.showFloorPlan && (
          <Dialog>
            <DialogTrigger asChild>
              <button className='bottom-xs left-xs bg-green absolute flex size-[36px] items-center justify-center rounded-full'>
                <img
                  src='https://preview-websitemodern-uni-hull.cloud.contensis.com/site-elements/icons/single-colour/ink-blue/grid-2.svg'
                  alt=''
                  role='presentation'
                  className='size-s'
                />
              </button>
            </DialogTrigger>

            <DialogContent
              className='border-0 bg-transparent !p-0 shadow-none'
              gallery={true}
            >
              <DialogHeader>
                <DialogTitle className='sr-only'>
                  {item.heading} Floorplan
                </DialogTitle>
              </DialogHeader>

              <div>
                <Image
                  image={data?.floorPlan[0]}
                  lazyLoad={true}
                  priority={'low'}
                />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className='py-2xs space-y-2xs-plus flex h-auto grow flex-col'>
        <div className='space-y-xs'>
          {item.building && <p className='tag'>{item.building.entryTitle}</p>}
          <h3 className='font-bold'>{item.heading || item.entryTitle}</h3>
          <div className='gap-xs mt-xs-plus flex flex-wrap'>
            {item?.bathroom && (
              <Tag
                data={{
                  label: item?.bathroom?.entryTitle,
                  taxonomy: undefined,
                  backgroundColour: 'light-aqua',
                  icon: {
                    asset: {
                      sys: {
                        uri: '/site-elements/icons/single-colour/ink-blue/toilet.svg'
                      }
                    }
                  },
                  link: undefined
                }}
              />
            )}
            {item?.catering && (
              <Tag
                data={{
                  label: item?.catering?.entryTitle,
                  taxonomy: undefined,
                  backgroundColour: 'light-aqua',
                  icon: {
                    asset: {
                      sys: {
                        uri: '/site-elements/icons/single-colour/ink-blue/cutlery-77.svg'
                      }
                    }
                  },
                  link: undefined
                }}
              />
            )}
            {item.accessible && (
              <Tag
                data={{
                  label: 'Accessible',
                  taxonomy: undefined,
                  backgroundColour: 'light-aqua',
                  icon: {
                    asset: {
                      sys: {
                        uri: '/site-elements/icons/single-colour/ink-blue/wheelchair.svg'
                      }
                    }
                  },
                  link: undefined
                }}
              />
            )}
          </div>
        </div>
        <Separator />
        <div className='space-y-m mt-2xs'>
          <p className='hidden sm:block'>{item.copy}</p>
          <div className='flex justify-between'>
            {item.weeklyPrice && (
              <div className='space-y-2xs'>
                <p className='tag'>Weekly price</p>
                <p>{`£${numberWithCommas(item.weeklyPrice)}`}</p>
              </div>
            )}
            {item.totalPrice && (
              <div className='space-y-2xs'>
                <p className='tag'>Total price</p>
                <p>{`£${numberWithCommas(item.totalPrice)}`}</p>
              </div>
            )}
            {item.occupancyDates && (
              <div className='space-y-2xs'>
                <p className='tag'>Occupancy</p>
                <p>{item.occupancyDates.entryTitle}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccommodationCard
